import React, { useState, useEffect } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { obtainTemplateCategoriesHelper } from '../../helpers/obtainTemplateCategoriesHelper';
import { hasSubcategories } from '../../helpers/hasSubcategories';
import { isBDCCategoryVisible } from '../../helpers/isBDCCategoryVisible';
import { TSideBarCategories } from './types';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import { BDC_AUTH } from 'src/constants/permissions';
import { READ_PERMISSION } from 'src/constants/permissions';
import { TEMPLATE_CATEGORY_BDC } from '../../constants/templates';
import { CategoriesList } from './CategoriesList';
import { SubcategoriesList } from './SubcategoriesList';

const useStyles = makeStyles((theme) => ({
    sidebar: {
        width: 350,
        flexShrink: 0,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2, 1),
        gap: theme.spacing(1),
        overflowY: 'auto',
        overflowX: 'hidden',
        maxHeight: '100%',
        '&::-webkit-scrollbar': {
            width: 8,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.disabled,
            borderRadius: 4,
            '&:hover': {
                backgroundColor: theme.palette.action.disabledBackground,
            },
        },
        [theme.breakpoints.down('sm')]: {
            width: 'auto',
            maxHeight: '30vh',
            borderRight: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
}));

export const SideBarCategories = ({
    setSelectedMenuOption,
    selectedMenuOption,
}: TSideBarCategories) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const [expandedBDC, setExpandedBDC] = useState(false);

    const templateCategories = obtainTemplateCategoriesHelper({
        translation: t,
        iconsShow: true,
    });

    const hasBDCReadPermission = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: BDC_AUTH,
        crud: READ_PERMISSION,
    });

    const handleMenuSelect = (optionId: string) => {
        setSelectedMenuOption(optionId);
    };

    const handleBDCClick = (e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        const bdcCategory = templateCategories.find(
            (cat) => cat.id === TEMPLATE_CATEGORY_BDC
        );

        if (
            bdcCategory?.subcategories &&
            bdcCategory.subcategories.length > 0
        ) {
            setExpandedBDC(!expandedBDC);
        } else {
            handleMenuSelect(TEMPLATE_CATEGORY_BDC);
        }
    };

    const handleSubcategoryClick = (subcategoryId: string) => {
        setSelectedMenuOption(subcategoryId);
    };

    useEffect(() => {
        const bdcCategory = templateCategories.find(
            (cat) => cat.id === TEMPLATE_CATEGORY_BDC
        );
        const isBDCSubcategory = bdcCategory?.subcategories?.some(
            (sub) => sub.id === selectedMenuOption
        );

        if (isBDCSubcategory && !expandedBDC) {
            setExpandedBDC(true);
        }
    }, [selectedMenuOption, templateCategories]);

    return (
        <Box className={classes.sidebar}>
            {templateCategories.map((category) => {
                if (!isBDCCategoryVisible(category.id, hasBDCReadPermission)) {
                    return null;
                }

                const isBDC = category.id === TEMPLATE_CATEGORY_BDC;
                const categoryHasSubcategories = hasSubcategories(category);

                return (
                    <Box key={category.id}>
                        <CategoriesList
                            category={category}
                            selectedMenuOption={selectedMenuOption}
                            hasSubcategories={categoryHasSubcategories}
                            isExpanded={isBDC ? expandedBDC : false}
                            onCategoryClick={
                                isBDC && categoryHasSubcategories
                                    ? handleBDCClick
                                    : () => handleMenuSelect(category.id)
                            }
                        />
                        {isBDC &&
                            categoryHasSubcategories &&
                            category.subcategories && (
                                <SubcategoriesList
                                    subcategories={category.subcategories}
                                    selectedMenuOption={selectedMenuOption}
                                    isExpanded={expandedBDC}
                                    onSubcategoryClick={handleSubcategoryClick}
                                />
                            )}
                    </Box>
                );
            })}
        </Box>
    );
};
